<script setup lang="ts">
import { computed } from "vue";

import actions from "~/actions";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import DocEditor from "~/components/text/DocEditor.vue";
import { XIcon } from "~/icons";
import { IconSize } from "~/shared/enums";
import type { Doc } from "~/shared/types";
import { useAppStore, usePageStore, useTenantStore } from "~/stores";
import { useAsyncDoc } from "~/utils/entityIncrementalLoading";

const props = defineProps<{
  duid: string;
  hideRemove?: boolean;
  borderless?: boolean;
  enableContextMenu?: boolean;
}>();

const emit = defineEmits<{
  remove: [doc: Doc];
  click: [event: MouseEvent];
  keydown: [event: KeyboardEvent];
}>();

const appStore = useAppStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const { doc, isLoading } = useAsyncDoc(computed(() => props.duid));

const onContextMenu = (event: MouseEvent) => {
  if (!doc.value || (tenantStore.isDart && !pageStore.adminHidden && event.altKey)) {
    return;
  }

  appStore.openContextMenu(event as PointerEvent, actions.context.doc(doc.value));
};

const onRemove = () => {
  if (!doc.value) {
    return;
  }

  emit("remove", doc.value);
};
</script>

<template>
  <div
    role="button"
    tabindex="0"
    class="group/doc-preview relative flex cursor-pointer flex-col rounded-sm"
    :class="[!borderless && 'border border-md', isLoading && 'h-[139px] justify-evenly']"
    @contextmenu="onContextMenu"
    @click="emit('click', $event)"
    @keydown="emit('keydown', $event)">
    <template v-if="isLoading">
      <LoadingSpinner />
      <div />
    </template>
    <template v-else-if="doc">
      <div class="relative flex items-center gap-2 rounded-t border-b border-md bg-lt px-2 py-1.5 text-sm">
        <PageIcon :page="doc" :size="IconSize.S" />
        <span class="truncate text-md">
          {{ doc.title }}
        </span>
        <div
          v-if="!hideRemove"
          class="absolute top-1/2 right-3 hidden translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm bg-lt group-hover/doc-preview:flex hover:bg-md"
          @click.stop="onRemove"
          @keydown.enter="onRemove">
          <XIcon class="icon-sm text-md" />
        </div>
      </div>
      <div class="relative h-24 overflow-hidden">
        <DocEditor :doc="doc" mini editor-classes="px-2 pt-2" />
        <div class="absolute inset-0 group-hover/doc-preview:bg-md/30" />
      </div>
    </template>
  </div>
</template>
